"use client";

import {
  MAX_COMMENT_ATTACHMENTS,
  type WorkplaceRequestCommentAttachment,
  type WorkplaceRequestCommentAttachmentInput
} from "@gettactic/api";
import type { GenerateUploadUrl } from "@gettactic/components/src/form/FileUploadInput";
import { uploadToSignedSlot } from "@gettactic/components/src/upload/uploadToSignedSlot";
import { getApiErrorMessage } from "@gettactic/helpers/src/errors/getApiErrorMessage";
import { attachmentUrlToPath } from "@gettactic/helpers/src/files/attachmentUrlToPath";
import {
  formatFileSize,
  isImageFile
} from "@gettactic/helpers/src/files/fileType";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

/** Largest file a comment attachment may be. The backend imposes no limit. */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** How many files upload at once, so a bulk drop doesn't saturate the network. */
const UPLOAD_CONCURRENCY = 3;

export type PendingAttachmentStatus = "uploading" | "ready" | "error";

export interface PendingAttachment {
  /** Stable client-side id; the list is keyed and addressed by it. */
  id: string;
  filename: string;
  status: PendingAttachmentStatus;
  /** Object path to submit; present once uploaded, or for a stored attachment. */
  path?: string;
  /** Local object URL or signed URL, used for the image thumbnail. */
  previewUrl?: string;
  byteSize?: number;
  errorMessage?: string;
}

interface UsePendingAttachmentsOptions {
  /** Endpoint that hands out signed upload slots (authenticated or guest). */
  generateUpload: GenerateUploadUrl;
  /** Attachments the comment already carries, when editing one. */
  initial?: WorkplaceRequestCommentAttachment[];
}

export interface UsePendingAttachmentsResult {
  attachments: PendingAttachment[];
  /** Queues files for upload, rejecting the ones over the size or count limit. */
  addFiles: (files: File[]) => void;
  remove: (id: string) => void;
  retry: (id: string) => void;
  /** Drops everything, e.g. after the comment has been posted. */
  reset: () => void;
  /** True while at least one upload is in flight. */
  isUploading: boolean;
  /** Attachments that uploaded successfully, in the API's input shape. */
  toInput: () => WorkplaceRequestCommentAttachmentInput[];
  /** Whether the list differs from the `initial` one it started from. */
  isDirty: boolean;
  /** Free slots left before {@link MAX_COMMENT_ATTACHMENTS} is reached. */
  remainingSlots: number;
}

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return `attachment-${idCounter}`;
};

/** Stored attachments start out ready; the ones with no usable path are dropped. */
const fromStored = (
  stored: WorkplaceRequestCommentAttachment[]
): PendingAttachment[] =>
  stored.flatMap((attachment) => {
    const path = attachmentUrlToPath(attachment.url);
    if (!path) {
      return [];
    }
    return [
      {
        id: nextId(),
        filename: attachment.filename || "Attachment",
        status: "ready" as const,
        path,
        previewUrl: isImageFile(attachment.filename, attachment.url)
          ? attachment.url
          : undefined
      }
    ];
  });

/**
 * Owns the files staged for a comment: validation, the upload queue, retries and
 * removal. Presentational components read {@link PendingAttachment} straight off
 * it, and the upload endpoint is injected so the same hook serves both the
 * authenticated composer and the share-link guest one.
 */
export function usePendingAttachments({
  generateUpload,
  initial
}: UsePendingAttachmentsOptions): UsePendingAttachmentsResult {
  const [attachments, setAttachments] = useState<PendingAttachment[]>(() =>
    initial ? fromStored(initial) : []
  );

  // Object URLs and files are kept out of state: they are upload plumbing, not
  // render input, and revoking them must survive re-renders.
  const objectUrlsRef = useRef(new Map<string, string>());
  const filesRef = useRef(new Map<string, File>());
  const queueRef = useRef<string[]>([]);
  const activeCountRef = useRef(0);
  // Mirrors how many attachments are staged, so the count limit can be applied
  // before creating object URLs rather than inside a state updater (which
  // React may run more than once).
  const stagedCountRef = useRef(attachments.length);
  const generateUploadRef = useRef(generateUpload);
  generateUploadRef.current = generateUpload;

  const releaseObjectUrl = useCallback((id: string) => {
    const objectUrl = objectUrlsRef.current.get(id);
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrlsRef.current.delete(id);
    }
    filesRef.current.delete(id);
  }, []);

  useEffect(
    () => () => {
      for (const objectUrl of objectUrlsRef.current.values()) {
        URL.revokeObjectURL(objectUrl);
      }
      objectUrlsRef.current.clear();
      filesRef.current.clear();
    },
    []
  );

  const patch = useCallback(
    (id: string, changes: Partial<PendingAttachment>) => {
      setAttachments((current) =>
        current.map((item) => (item.id === id ? { ...item, ...changes } : item))
      );
    },
    []
  );

  const pump = useCallback(() => {
    while (
      activeCountRef.current < UPLOAD_CONCURRENCY &&
      queueRef.current.length > 0
    ) {
      const id = queueRef.current.shift();
      if (!id) {
        break;
      }
      const file = filesRef.current.get(id);
      if (!file) {
        continue;
      }

      activeCountRef.current += 1;
      uploadToSignedSlot(file, (filename) =>
        generateUploadRef.current(filename)
      )
        .then((uploaded) => {
          patch(id, { status: "ready", path: uploaded.path });
        })
        .catch((error) => {
          patch(id, {
            status: "error",
            errorMessage: getApiErrorMessage(error, "Upload failed")
          });
        })
        .finally(() => {
          activeCountRef.current -= 1;
          pump();
        });
    }
  }, [patch]);

  const enqueue = useCallback(
    (ids: string[]) => {
      queueRef.current.push(...ids);
      pump();
    },
    [pump]
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      const accepted: PendingAttachment[] = [];
      const tooLarge: string[] = [];
      let overflowed = 0;
      let room = MAX_COMMENT_ATTACHMENTS - stagedCountRef.current;

      for (const file of files) {
        if (room <= 0) {
          overflowed += 1;
          continue;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          tooLarge.push(file.name);
          continue;
        }

        const id = nextId();
        filesRef.current.set(id, file);

        let previewUrl: string | undefined;
        if (file.type.startsWith("image/")) {
          previewUrl = URL.createObjectURL(file);
          objectUrlsRef.current.set(id, previewUrl);
        }

        accepted.push({
          id,
          filename: file.name,
          status: "uploading",
          previewUrl,
          byteSize: file.size
        });
        room -= 1;
      }

      if (accepted.length > 0) {
        stagedCountRef.current += accepted.length;
        setAttachments((current) => [...current, ...accepted]);
      }

      if (tooLarge.length > 0) {
        toast.error(
          `${tooLarge.join(", ")} ${tooLarge.length > 1 ? "are" : "is"} over the ${formatFileSize(MAX_ATTACHMENT_BYTES)} limit`
        );
      }
      if (overflowed > 0) {
        toast.error(
          `A comment can carry at most ${MAX_COMMENT_ATTACHMENTS} attachments`
        );
      }
      if (accepted.length > 0) {
        enqueue(accepted.map((item) => item.id));
      }
    },
    [enqueue]
  );

  const remove = useCallback(
    (id: string) => {
      queueRef.current = queueRef.current.filter((queued) => queued !== id);
      releaseObjectUrl(id);
      stagedCountRef.current = Math.max(0, stagedCountRef.current - 1);
      setAttachments((current) => current.filter((item) => item.id !== id));
    },
    [releaseObjectUrl]
  );

  const retry = useCallback(
    (id: string) => {
      if (!filesRef.current.has(id)) {
        return;
      }
      patch(id, { status: "uploading", errorMessage: undefined });
      enqueue([id]);
    },
    [enqueue, patch]
  );

  const reset = useCallback(() => {
    queueRef.current = [];
    for (const objectUrl of objectUrlsRef.current.values()) {
      URL.revokeObjectURL(objectUrl);
    }
    objectUrlsRef.current.clear();
    filesRef.current.clear();
    stagedCountRef.current = 0;
    // Bail when there is nothing staged: reset also runs on every navigation,
    // and a fresh array would re-render the composer for no reason.
    setAttachments((current) => (current.length === 0 ? current : []));
  }, []);

  // Cheap enough to derive every render, and memoising it would only add a
  // dependency on `initial`, whose identity changes on every comments refetch.
  const initialPaths = (initial ?? []).flatMap(
    (attachment) => attachmentUrlToPath(attachment.url) ?? []
  );

  const readyPaths = useMemo(
    () =>
      attachments.flatMap((item) =>
        item.status === "ready" && item.path ? [item.path] : []
      ),
    [attachments]
  );

  const toInput = useCallback(
    (): WorkplaceRequestCommentAttachmentInput[] =>
      attachments.flatMap((item) =>
        item.status === "ready" && item.path
          ? [{ path: item.path, filename: item.filename }]
          : []
      ),
    [attachments]
  );

  return {
    attachments,
    addFiles,
    remove,
    retry,
    reset,
    isUploading: attachments.some((item) => item.status === "uploading"),
    toInput,
    isDirty:
      readyPaths.length !== initialPaths.length ||
      readyPaths.some((path, position) => path !== initialPaths[position]),
    remainingSlots: Math.max(0, MAX_COMMENT_ATTACHMENTS - attachments.length)
  };
}
